/**
 * The portal's permission catalog: the backend's permission constants, verbatim. Client gating is
 * UX only — the server is the enforcement point — so this list exists to hide affordances, never to
 * protect data. It is the whole contract, not a portal subset: the subset a build actually enforces
 * is whatever the code references in a guard, and a second "portal-only" list is a second source of
 * truth to drift. Mirror of `contracts/authz/permissions.json` (backend repo); the drift test pins it
 * against `permissions.fixture.ts`.
 */
export const PERMISSIONS = [
  // device (18)
  'device.archive',
  'device.bulk_upload_result_read',
  'device.checkin',
  'device.export_csv',
  'device.imei_swap',
  'device.lock_bulk',
  'device.lock_bulk_approve',
  'device.lock_full',
  'device.lock_partial',
  'device.message',
  'device.offline_code_generate',
  'device.payment_notify',
  'device.read',
  'device.register',
  'device.register_bulk',
  'device.release',
  'device.release_bulk',
  'device.unlock',
  // tenant (13)
  'tenant.branding_read',
  'tenant.branding_write',
  'tenant.config_read',
  'tenant.config_write',
  'tenant.create',
  'tenant.machine_identity_manage',
  'tenant.message_template_manage',
  'tenant.offboard',
  'tenant.schedule_manage',
  'tenant.update',
  'tenant.webhook_manage',
  'tenant.whitelist_manage',
  'tenant.whitelist_read',
  // platform (5)
  'platform.config_read',
  'platform.config_write',
  'platform.health_read',
  'platform.integrity_alert_read',
  'platform.integrity_alert_resolve',
  // workflow (5)
  'workflow.manage',
  'workflow.migrate',
  'workflow.migrate_approve',
  'workflow.pause_resume',
  'workflow.read',
  // user (5)
  'user.invite',
  'user.manage',
  'user.read',
  'user.release_authority_grant',
  'user.role_assign',
  // role (1)
  'role.read',
  // delegation (3)
  'delegation.grant',
  'delegation.read',
  'delegation.revoke',
  // enrolment (3)
  'enrolment.create',
  'enrolment.handover',
  'enrolment.read',
  // plan (2)
  'plan.manage',
  'plan.read',
  // app_release (2)
  'app_release.publish',
  'app_release.read',
  // search (2)
  'search.configurations',
  'search.read',
  // singletons
  'audit_log.read',
  'dashboard.read',
  'payment_event.read',
  'profile.manage',
  'provisioning_queue.read',
] as const;

/** Every permission string the portal understands. Anything outside this union is denied. */
export type Permission = (typeof PERMISSIONS)[number];

const PERMISSION_SET: ReadonlySet<string> = new Set(PERMISSIONS);

/** Narrows an arbitrary claim string to a known permission; unknown strings fail closed. */
export function isPermission(value: string): value is Permission {
  return PERMISSION_SET.has(value);
}
